import { Info, Launch } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { v4 as uuid } from "uuid";
import { OrganizationAPI, OrganizationRoleAPI } from "../../../api";
import DeleteOrganizationDialog from "../../../components/dialogs/DeleteOrganizationDialog";
import PATHS from "../../../components/navigation/_paths";
import useFeature from "../../../hooks/useFeature";
import useIsAdmin from "../../../hooks/useIsAdmin";
import keyify from "../../../lib/keyify";
import { OrganizationRole } from "../../../model/OrganizationRole";
import { ManageBrand } from "../components/ManageBrand";
import ThemePicker from "../components/OrganizationThemePicker";
import { useUpdateOrganizationProps } from "../components/UpdateOrganizationContext";

interface RolesDiff {
  addRoles: OrganizationRole[];
  removeRoles: OrganizationRole[];
}

const OrganizationGeneralSettings: FC<unknown> = () => {
  const {
    organization: sourceOrganization,
    onError,
    onSubmit,
    disabled,
    buttonAlignment,
    buttonText,
    onOrganizationDeleted,
    isCreatingOrganization,
  } = useUpdateOrganizationProps();

  const canManageOrganizationRoles = useFeature("organizationRoles");
  const [loading, setLoading] = useState<boolean>(false);
  const canAdministrate = useIsAdmin();
  const navigate = useNavigate();
  const [organizationRoles, setOrganizationRoles] = useState<
    OrganizationRole[]
  >([]);
  const [currentOrganizationRoles, setCurrentOrganizationRoles] = useState<
    OrganizationRole[]
  >([]);
  const [currentOrganizationRolesLoaded, setCurrentOrganizationRolesLoaded] =
    useState(false);
  const [rolesDiff, setRolesDiff] = useState<RolesDiff>({
    addRoles: [],
    removeRoles: [],
  });

  useEffect(() => {
    if (!canAdministrate && !isCreatingOrganization) {
      navigate(PATHS.ORGANIZATION_EXECUTIONS.link);
    }
  });

  // State used for the organization deletion dialog
  const [
    confirmOrganizationDeleteDialogOpen,
    setConfirmDeleteOrganizationDialogOpen,
  ] = useState<boolean>(false);
  const [organization, setOrganization] = useState(sourceOrganization);

  // State to hold the organization logo
  const [logo, setLogo] = useState<string | undefined>(
    sourceOrganization.theme?.shortLogoUrl,
  );

  // State to hold the organization's long logo
  const [longLogo, setLongLogo] = useState<string | undefined>(
    sourceOrganization.theme?.longLogoUrl,
  );

  // State to hold the organization's favicon
  const [favicon, setFavicon] = useState<string | undefined>(
    sourceOrganization.theme?.faviconUrl,
  );

  // State to keep track of when a key is manually changed by the user overriding the generated value
  const [keyEntered, setKeyEntered] = useState(false);

  // State to hold error for data confirmation
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setOrganization(sourceOrganization);
    setLogo(sourceOrganization.theme?.shortLogoUrl);
    setLongLogo(sourceOrganization.theme?.longLogoUrl);
    setFavicon(sourceOrganization.theme?.faviconUrl);
  }, [sourceOrganization]);

  const handleOrganizationRoleUpdate = (
    event: unknown,
    value: OrganizationRole[],
  ) => {
    // get a diff (for add and remove endpoints)

    const addRoles = value.filter(
      (role) => !currentOrganizationRoles.some((r) => r.id === role.id),
    );

    const removeRoles = currentOrganizationRoles.filter(
      (role) => !value.some((r) => r.id === role.id),
    );

    setCurrentOrganizationRoles(value);

    setRolesDiff({
      addRoles,
      removeRoles,
    });
  };

  useEffect(() => {
    if (!canManageOrganizationRoles) return;

    OrganizationRoleAPI.list().then((roles) => {
      setOrganizationRoles(roles);
    });
  }, [canManageOrganizationRoles]);

  useEffect(() => {
    if (!canManageOrganizationRoles) return;

    if (organization?.id) {
      setCurrentOrganizationRolesLoaded(false);

      OrganizationRoleAPI.byOrganization(organization.id).then((roles) => {
        setCurrentOrganizationRoles(roles);
        setCurrentOrganizationRolesLoaded(true);
      });
    }
  }, [canManageOrganizationRoles, organization.id]);

  const handleSubmit = async () => {
    if (!organization.name) {
      setError("Organization name is required");
      if (onError) {
        onError("Organization name is required");
      }
      return;
    }

    if (!organization.key) {
      setError("Organization key is required");
      if (onError) {
        onError("Organization key is required");
      }
      return;
    }

    if (rolesDiff.removeRoles.length > 0) {
      rolesDiff.removeRoles.forEach((role) => {
        OrganizationRoleAPI.unassignToOrganization(role.id, organization.id);
      });
    }

    if (rolesDiff.addRoles.length > 0) {
      rolesDiff.addRoles.forEach((role) => {
        OrganizationRoleAPI.assignToOrganization(role.id, organization.id);
      });
    }

    // Check if the key already exists and reject if true
    try {
      const match = await OrganizationAPI.byKey(organization.key);
      if (organization.id != match.id) {
        // If this is same org, ignore
        // Otherwise, the key exists on another org
        setError("Organization key exists! Please choose another.");
        if (onError) {
          onError("Organization key exists! Please choose another.");
        }
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // If we get a 404, the key is available (good)
      if (err.response && err.response.status === 404) {
        // Continue with submit - key is available
      } else {
        // Handle other errors
        setError("Error checking organization key");
        if (onError) {
          onError("Error checking organization key");
        }
        return;
      }
    }

    setError(undefined);
    setLoading(true);
    try {
      await onSubmit({
        ...organization,
        id: organization.id ?? uuid(),
        owner_id: organization.owner_id ?? uuid(),
        key: organization.key ?? keyify(organization.name),
        theme: {
          ...organization.theme,
          shortLogoUrl: logo,
          longLogoUrl: longLogo,
          faviconUrl: favicon,
        },
      });

      setLoading(false);
      setError(undefined);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);
      setLoading(false);
      setError(`Failure: ${err.message}`);
      if (onError) {
        onError(err);
      }
    }
  };

  if (organization)
    return (
      <Grid container spacing={3}>
        <Grid size={8}>
          <Typography variant="h2">Organization</Typography>
        </Grid>
        <Grid size={4}>
          <Box display="flex" justifyContent="flex-end">
            <Tooltip title="Publicly accessible link to public portal for this organization">
              <Chip
                label="Portal"
                deleteIcon={<Launch />}
                onDelete={() => {
                  /* */
                }}
                target="_blank"
                component="a"
                href={`/${organization.key}`}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label={"Name"}
            value={organization.name}
            disabled={disabled}
            onChange={(e) => {
              setKeyEntered(false);
              setOrganization((org) => ({
                ...org,
                name: e.target.value,
                key: keyEntered ? organization.key : keyify(e.target.value),
              }));
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label={"Organization Handle"}
            value={organization.key}
            disabled={disabled}
            onChange={(e) => {
              setKeyEntered(true);
              setOrganization((org) => ({ ...org, key: e.target.value }));
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="A unique, human-readable handle for your organization.  This will be used to create your unique URL. The handle cannot be changed unless you update your organization's name.">
                    <IconButton>
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
              readOnly: organization.name == sourceOrganization.name,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <TextField
            fullWidth
            label={"Description"}
            size="small"
            disabled={disabled}
            value={organization.description ?? ""}
            onChange={(e) =>
              setOrganization((org) => ({
                ...org,
                description: e.target.value,
              }))
            }
          />
        </Grid>
        {/*
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label={"Billing Email"}
            size="small"
            disabled={!isCreatingOrganization}
            value={organization.billing_email ?? ""}
            onChange={(e) =>
              setOrganization((org) => ({
                ...org,
                billing_email: e.target.value,
              }))
            }
          />
        </Grid>
        */}
        {canManageOrganizationRoles && (
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <Autocomplete
              multiple
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization Type"
                  placeholder="Add Roles"
                />
              )}
              value={currentOrganizationRoles}
              loading={!currentOrganizationRolesLoaded}
              onChange={handleOrganizationRoleUpdate}
              size="small"
              options={organizationRoles}
              isOptionEqualToValue={(option, value) => {
                return option.id === value.id;
              }}
              getOptionLabel={(option) => option.name}
            />
          </Grid>
        )}

        <Grid size={12}>
          <Typography variant="h2">Brand Logo</Typography>
        </Grid>
        <Box
          sx={{
            width: "100%",
            mx: 4,
            my: 2,
          }}
        >
          <Card
            sx={(theme) => ({
              px: 2,
              borderRadius: 4,
              boxShadow: 3,
              backgroundColor: theme.palette.grey[100],
            })}
          >
            <CardContent>
              <ManageBrand
                alignment="space-between"
                cellSize="200px"
                organization={organization}
                onUpdate={(fav, short, long) => {
                  setFavicon(fav);
                  setLogo(short);
                  setLongLogo(long);
                }}
              />
            </CardContent>
          </Card>
        </Box>

        {
          <>
            <Grid size={12}>
              <Typography variant="h2">Brand Theme</Typography>
            </Grid>
            <Box
              sx={{
                width: "100%",
                mx: 3,
                my: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <ThemePicker
                organization={organization}
                onUpdate={(o) => setOrganization(o)}
              />
              <ThemePicker
                colorType="secondary"
                organization={organization}
                onUpdate={(o) => setOrganization(o)}
              />
            </Box>
          </>
        }
        {/*themeEditorFeatureFlag && (
          <>
            <Grid item xs={12}>
              <Typography variant="h2">Configuration</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Theme</Typography>
              <CodeMirror
                value={JSON.stringify(organization.theme, null, "  ") ?? ""}
                height="200px"
                extensions={[json()]}
                aria-disabled={disabled}
                onChange={(value) => {
                  try {
                    const parsed = JSON.parse(value);
                    setOrganization((org) => ({ ...org, theme: parsed }));
                  } catch (e) {
                    // do nothing
                    console.error("invalid JSON!", e);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Config</Typography>
              <CodeMirror
                value={JSON.stringify(organization.config, null, "  ") ?? ""}
                height="200px"
                extensions={[json()]}
                aria-disabled={disabled}
                onChange={(value) => {
                  try {
                    const parsed = JSON.parse(value);
                    setOrganization((org) => ({ ...org, config: parsed }));
                  } catch (e) {
                    // do nothing
                    console.error("invalid JSON!", e);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Well Known Processes</Typography>
              <CodeMirror
                value={
                  JSON.stringify(
                    organization.well_known_processes,
                    null,
                    "  ",
                  ) ?? ""
                }
                height="200px"
                extensions={[json()]}
                aria-disabled={disabled}
                onChange={(value) => {
                  try {
                    const parsed = JSON.parse(value);
                    setOrganization((org) => ({
                      ...org,
                      well_known_processes: parsed,
                    }));
                  } catch (e) {
                    // do nothing
                    console.error("invalid JSON!", e);
                  }
                }}
              />
            </Grid>
          </>
        )*/}
        <Grid size={12}>
          <Box
            sx={{
              my: 4,
              display: "flex",
              justifyContent: buttonAlignment === "right" ? "right" : "left",
            }}
          >
            {loading ? (
              <CircularProgress />
            ) : (
              <Button
                variant="contained"
                disabled={disabled}
                onClick={handleSubmit}
              >
                {buttonText ?? "Submit"}
              </Button>
            )}
          </Box>
        </Grid>
        {error && (
          <Box sx={{ my: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {onOrganizationDeleted && (
          <>
            <Grid size={12}>
              <Typography variant="h2">Danger Zone</Typography>
            </Grid>
            <Grid size={12}>
              <Box
                sx={{
                  my: 4,
                  display: "flex",
                  justifyContent:
                    buttonAlignment === "right" ? "right" : "left",
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setConfirmDeleteOrganizationDialogOpen(true)}
                >
                  Delete Organization
                </Button>
              </Box>
            </Grid>
            <DeleteOrganizationDialog
              open={confirmOrganizationDeleteDialogOpen}
              organization={organization}
              setDialogOpen={setConfirmDeleteOrganizationDialogOpen}
              onOrganizationDeleted={onOrganizationDeleted}
            />
          </>
        )}
      </Grid>
    );

  /*  return (
      <>
        <Grid item xs={8}>
          <Typography variant="h2">Organization</Typography>
        </Grid>
        <Grid item xs={4}>
          <Box display="flex" justifyContent="flex-end">
            <Tooltip title="Publicly accessible link to public portal for this organization">
              <Chip
                label="Portal"
                deleteIcon={<Launch />}
                onDelete={() => {
                }}
                target="_blank"
                component="a"
                href={`/${organization.key}`}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label={"Name"}
            value={organization.name}
            disabled={disabled}
            onChange={(e) => {
              setOrganization((org: Organization) => ({
                ...org,
                name: e.target.value,
                key: keyEntered ? organization.key : keyify(e.target.value),
              }));
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label={"Organization Handle"}
            value={organization.key}
            disabled={disabled}
            onChange={(e) => {
              setKeyEntered(true);
              setOrganization((org) => ({ ...org, key: e.target.value }));
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="A unique, human-readable handle for your organization.  This will be used to create your unique URL. The handle cannot be changed.">
                    <IconButton>
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
              readOnly: !!organization?.id,
            }}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label={"Description"}
            size="small"
            disabled={disabled}
            value={organization.description ?? ""}
            onChange={(e) =>
              setOrganization(
                (org) =>
                  ({
                    ...org,
                    description: e.target.value,
                  }) as Organization,
              )
            }
          />
        </Grid>
        {!emailDisabled && (
          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              label={"Email"}
              size="small"
              disabled={disabled}
              value={organization.email ?? ""}
              onChange={(e) =>
                setOrganization((org) => ({ ...org, email: e.target.value }))
              }
            />
          </Grid>
        )}
        <Grid item xs={12} md={12}>
          <TextField
            fullWidth
            label={"Billing Email"}
            size="small"
            disabled={!isCreatingOrganization}
            value={organization.billing_email ?? ""}
            onChange={(e) =>
              setOrganization(
                (org) =>
                  ({
                    ...org,
                    billing_email: e.target.value,
                  }) as Organization,
              )
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              my: 4,
              display: "flex",
              justifyContent: buttonAlignment === "right" ? "right" : "left",
            }}
          >
            {loading ? (
              <CircularProgress />
            ) : (
              <Button
                variant="contained"
                disabled={disabled}
                onClick={handleSubmit}
              >
                {buttonText ?? "Submit"}
              </Button>
            )}
          </Box>
        </Grid>
        {error && (
          <Box sx={{ my: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
      </>
    );*/
};

export { OrganizationGeneralSettings };
